import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { STUDIO_SETS_GALLERY } from "@/lib/lime-data";

const SETS = Object.keys(STUDIO_SETS_GALLERY);

export default function StudioSetsGallery() {
  const [active, setActive] = useState(SETS[0]);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const set = STUDIO_SETS_GALLERY[active];
  const images = set.images;
  const lightbox = lightboxIdx !== null ? images[lightboxIdx] : null;

  const goPrev = (e) => {
    if (e) e.stopPropagation();
    setLightboxIdx((i) => (i === 0 ? images.length - 1 : i - 1));
  };
  const goNext = (e) => {
    if (e) e.stopPropagation();
    setLightboxIdx((i) => (i === images.length - 1 ? 0 : i + 1));
  };

  useEffect(() => {
    if (lightboxIdx === null) return;
    const total = images.length;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") {
        setLightboxIdx((i) => (i === 0 ? total - 1 : i - 1));
      } else if (e.key === "ArrowRight") {
        setLightboxIdx((i) => (i === total - 1 ? 0 : i + 1));
      } else if (e.key === "Escape") {
        setLightboxIdx(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, images.length]);

  // Reset lightbox when switching tabs
  useEffect(() => { setLightboxIdx(null); }, [active]);

  return (
    <section id="studio-sets" className="py-24 sm:py-32 border-t border-[#262626]" data-testid="studio-sets-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="font-mono-overline text-[#E4FF00]">Studio Sets · Spaces</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-3">
              Step inside.
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {SETS.length > 1 && SETS.map((s) => (
              <button
                key={s}
                onClick={() => setActive(s)}
                data-testid={`studio-set-tab-${s.toLowerCase()}`}
                className={`px-5 py-2.5 text-xs font-mono uppercase tracking-widest border transition-colors ${
                  active === s
                    ? "bg-[#E4FF00] text-black border-[#E4FF00]"
                    : "border-[#262626] text-[#A3A3A3] hover:border-[#404040]"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <p className="max-w-2xl text-sm text-[#A3A3A3] leading-relaxed mb-8" data-testid="studio-set-description">
          {set.description}
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-5 gap-2.5" data-testid="studio-set-grid">
          {images.map((img, idx) => (
            <button
              key={`${active}-${idx}`}
              onClick={() => setLightboxIdx(idx)}
              data-testid={`studio-set-tile-${idx}`}
              className="group relative aspect-[3/4] overflow-hidden border border-[#262626] hover:border-[#E4FF00]/60 transition-colors"
            >
              <img
                src={img.src}
                alt={img.caption}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute left-3 bottom-3 right-3 text-left opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="font-mono-overline text-[#E4FF00]">{active}</div>
                <div className="font-display font-bold text-sm text-[#f4f4f0] mt-1">{img.caption}</div>
              </div>
            </button>
          ))}
        </div>

        {images.length === 0 && (
          <div className="border border-dashed border-[#262626] py-20 text-center text-[#666] font-mono text-xs uppercase tracking-widest">
            Photos coming soon
          </div>
        )}
      </div>

      {lightbox && (
        <div
          onClick={() => setLightboxIdx(null)}
          data-testid="studio-set-lightbox"
          className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4 cursor-zoom-out"
        >
          <img
            src={lightbox.src}
            alt={lightbox.caption}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[92vw] object-contain border border-[#262626] cursor-default"
          />

          {images.length > 1 && (
            <>
              <button
                onClick={goPrev}
                aria-label="Previous"
                data-testid="studio-set-lightbox-prev"
                className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 h-12 w-12 sm:h-14 sm:w-14 flex items-center justify-center bg-[#141414]/80 border border-[#262626] text-[#f4f4f0] hover:bg-[#E4FF00] hover:text-black hover:border-[#E4FF00] transition-colors"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={goNext}
                aria-label="Next"
                data-testid="studio-set-lightbox-next"
                className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 h-12 w-12 sm:h-14 sm:w-14 flex items-center justify-center bg-[#141414]/80 border border-[#262626] text-[#f4f4f0] hover:bg-[#E4FF00] hover:text-black hover:border-[#E4FF00] transition-colors"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center pointer-events-none">
            <div className="font-mono-overline text-[#E4FF00]">{active} · {lightboxIdx + 1}/{images.length}</div>
            <div className="font-display font-bold text-[#f4f4f0] mt-1">{lightbox.caption}</div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            data-testid="studio-set-lightbox-close"
            className="absolute top-6 right-6 text-[#f4f4f0] text-sm font-mono uppercase tracking-widest hover:text-[#E4FF00] transition-colors"
          >
            Close ✕
          </button>
        </div>
      )}
    </section>
  );
}
